import Pause from '@mui/icons-material/Pause'
import PlayArrow from '@mui/icons-material/PlayArrow'
import { Fade } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import bellSound from 'src/assets/audio/bell.mp3'
import TimerStartOverlay from '../TimerStartOverlay/TimerStartOverlay'
import FixedTimer from './FixedTimer/FixedTimer'
import InfiniteTimer from './InfiniteTimer/InfiniteTimer'
import RangeTimer from './RangeTimer/RangeTimer'
import {
  CountdownContainer,
  FinishPracticeButton,
  MainTimerContainer,
  PausePlayIconButton,
  PracticeTimerButtonsContainer,
  PracticeTimerContainer,
} from './styledComponents'
import { getTimerCompletionDetails } from './utils'

const timerStates = {
  stop: 'stopped',
  start: 'started',
}

export const overlayPageStates = {
  landing: 'landing',
  countDown: 'countDown',
}

export default function TimerController({
  title,
  description,
  durationType,
  intervals,
  minDuration,
  maxDuration,
  duration,
  bellType,
  timerBackground,
  beginButtonOverride,
  pausePlayButtonRef,
  onTimerFinish,
  backToPreviousPage,
}) {
  const bellAudio = useMemo(() => {
    return new Audio(bellSound)
  }, [])
  const [audioSoundsLog, setAudioSoundsLog] = useState({
    startSound: 0,
  })

  const [startTime, setStartTime] = useState(null)
  const [pauseStartTime, setPauseStartTime] = useState(null)

  const [timerSeconds, setTimerSeconds] = useState(0)
  const [countDown, setCountDown] = useState(1)
  const [pauseTime, setPauseTime] = useState(0)

  const [timerState, setTimerState] = useState('stopped')

  const [interval, setIntervalState] = useState(null)
  // const [pauseInterval, setPauseInterval] = useState(null)
  const [countDownInterval, setCountDownInterval] = useState(null)

  const [showCountDown, setShowCountDown] = useState(true)

  const [overlayPageState, setOverlayPageState] = useState(overlayPageStates.landing)

  const intervalsLeft = useMemo(() => {
    return intervals?.filter((interval) => interval / 1000 > timerSeconds)
  }, [timerSeconds, intervals])

  const TimerTypeComponent = useMemo(() => {
    console.log({ durationType })
    switch (durationType) {
      case 'range':
        return RangeTimer
      case 'fixed':
        return FixedTimer
      default:
        //return default as unlimited
        return InfiniteTimer
    }
  }, [durationType])

  function playStartTimerSound(forcePlay = false) {
    if (bellType !== 'bell') return
    if (audioSoundsLog?.startSound > 0 && !forcePlay) return

    setAudioSoundsLog((prev) => ({ ...prev, startSound: prev.startSound + 1 }))
    bellAudio.play()
  }

  function updateTime(stTime) {
    ///todo: diffeence between now time and start time and also subtract the pause time from this.
    const currentTime = dayjs()
    const timeDiff = currentTime.diff(stTime, 'seconds')
    setTimerSeconds(timeDiff)
  }

  function startTimer(stTime = startTime, pStartTime = pauseStartTime) {
    const now = dayjs()

    setTimerState(timerStates?.start)
    ///todo: calculate pause time in seconds whenever the timerstarts

    const updatedStartTime = stTime?.add(
      pStartTime ? now.diff(pStartTime, 'milliseconds') : 0,
      'milliseconds',
    )
    setStartTime(updatedStartTime)

    setPauseStartTime(null)
    // setPauseTime(totalPausedTime)

    setIntervalState(setInterval(() => updateTime(updatedStartTime), 500))
  }

  function handleStartTimerButtonClick() {
    startTimer()
  }

  function stopTimer() {
    setTimerState(timerStates?.stop)
    setPauseStartTime(dayjs())
    clearInterval(interval)
  }

  function beginPracticeTimer() {
    setCountDownInterval(
      setInterval(() => {
        setCountDown((prev) => prev + 1)
      }, [1000]),
    )
    setOverlayPageState(overlayPageStates.countDown)
    bellAudio.pause()
  }

  function handleTimerFinish() {
    stopTimer()
    const timerStatusDetails = {
      meditationDuration: timerSeconds * 1000,
      isCompleted: getTimerCompletionDetails(durationType, duration, minDuration, timerSeconds),
      serviceType: 'self',
    }
    onTimerFinish(timerStatusDetails)
  }

  function checkAndPlayIntervalSound() {
    if (bellType !== 'bell') return
    if (intervals?.some((timerInterval) => timerInterval / 1000 === timerSeconds)) bellAudio.play()
  }

  useEffect(() => {
    checkAndPlayIntervalSound()
  }, [timerSeconds])

  useEffect(() => {
    return () => {
      if (interval) clearInterval(interval)
      if (countDownInterval) clearInterval(countDownInterval)
    }
  }, [])

  useEffect(() => {
    if (countDown > 3) {
      clearInterval(countDownInterval)
      setShowCountDown(false)
      // setCountDown(1)
      playStartTimerSound()
      startTimer(dayjs())
      // setOverlayPageState(overlayPageStates.landing)
    }
  }, [countDown])

  return (
    <PracticeTimerContainer>
      <Fade in={showCountDown} unmountOnExit>
        <CountdownContainer>
          <TimerStartOverlay
            beginTimer={
              beginButtonOverride
                ? () => {
                    beginButtonOverride(beginPracticeTimer)
                  }
                : beginPracticeTimer
            }
            backToPreviousPage={backToPreviousPage}
            overlayPageState={overlayPageState}
            timerData={{
              title,
              description,
              duration,
              durationType,
              intervals,
              maxDuration,
              minDuration,
            }}
            countDown={countDown}
          />
        </CountdownContainer>
      </Fade>
      <Fade in={!showCountDown} unmountOnExit>
        <MainTimerContainer>
          <div></div>
          <TimerTypeComponent
            {...{ minDuration, maxDuration, duration, playStartTimerSound }}
            timerSeconds={timerSeconds}
            backgroundImg={timerBackground}
            totalIntervals={intervals}
            intervalsLeft={intervalsLeft}
          />
          <PracticeTimerButtonsContainer>
            <PausePlayIconButton
              ref={pausePlayButtonRef}
              onClick={() => {
                timerState === timerStates.stop ? handleStartTimerButtonClick() : stopTimer()
              }}
            >
              {timerState === timerStates.stop ? (
                <PlayArrow sx={{ fontSize: 'inherit' }} />
              ) : (
                <Pause sx={{ fontSize: 'inherit' }} />
              )}
            </PausePlayIconButton>
            <FinishPracticeButton onClick={handleTimerFinish}>Finish</FinishPracticeButton>
          </PracticeTimerButtonsContainer>
        </MainTimerContainer>
      </Fade>
    </PracticeTimerContainer>
  )
}
// <CountdownContainer>
//   {overlayPageState === overlayPageStates.countDown && (
//     <>
//       <div></div>
//       <div className="d-flex justify-content-center align-items-center gap-1">
//         <span className="countdown-timer">{3 - countDown + 1}</span>
//       </div>
//       <div></div>
//     </>
//   )}
//   {overlayPageState === overlayPageStates.landing && (
//     <>
//       <div className="d-flex justify-content-start w-100">
//         <BackButton onClick={backToPreviousPage}>
//           <ArrowBack /> <span>Back</span>
//         </BackButton>
//       </div>
//       <div className="d-flex justify-content-center align-items-center flex-column gap-1">
//         <span className="small-heading">Practice Timer</span>
//         <CHeading1 style={{ fontSize: 'calc(1.875rem + 1.5vw)' }}>{title}</CHeading1>
//       </div>
//       <BeginPracticeButton
//         onClick={
//           beginButtonOverride
//             ? () => {
//                 beginButtonOverride(beginPracticeTimer)
//               }
//             : beginPracticeTimer
//         }
//       >
//         Begin
//       </BeginPracticeButton>
//     </>
//   )}
// </CountdownContainer>
